import { TablePage, dataFormatter, Dialog } from 'web-mojo';
import { FirmwareUpdateList, FIRMWARE_UPDATE_BADGES, cancelQueuedUpdate } from '@portal/models/FirmwareUpdate.js';

export default class FirmwareUpdatesPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'OTA Activity',
            icon: 'bi-activity',
            pageName: 'firmware_updates',
            route: 'group/firmware/updates',
            permissions: ['manage_firmware', 'manage_cam_devices', 'view_cam_devices', 'manage_group', 'manage_groups'],
            requiresGroup: true,
            // FirmwareUpdate filters by device__group, not group
            groupField: 'device__group',

            Collection: FirmwareUpdateList,

            defaultQuery: {
                sort: '-created',
                // graph=default nests device (name/uuid) and build
                graph: 'default',
            },

            columns: [
                {
                    label: 'When',
                    key: 'created',
                    formatter: 'relative',
                },
                {
                    label: 'Device',
                    key: 'device',
                    formatter: (val, row) => {
                        const dev = row?.device;
                        if (!dev) return '<span class="text-muted small">—</span>';
                        if (typeof dev === 'object') return dev.name || dev.uuid || `Device #${dev.id}`;
                        return `Device #${dev}`;
                    },
                },
                {
                    label: 'Update',
                    key: 'to_version',
                    // from_version/to_version are device-reported (audit #293 H1) — escape before interpolating into HTML
                    formatter: (val, row) => {
                        const from = dataFormatter.escapeHtml(row?.from_version || '?');
                        const to = dataFormatter.escapeHtml(row?.to_version || '?');
                        return `<span class="font-monospace small">${from} <i class="bi bi-arrow-right"></i> ${to}</span>`;
                    },
                },
                {
                    label: 'Status',
                    key: 'status',
                    formatter: FIRMWARE_UPDATE_BADGES,
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Queued', value: 'queued' },
                            { label: 'Sent', value: 'sent' },
                            { label: 'Started', value: 'started' },
                            { label: 'Downloading', value: 'downloading' },
                            { label: 'Downloaded', value: 'downloaded' },
                            { label: 'Verifying', value: 'verifying' },
                            { label: 'Applying', value: 'applying' },
                            { label: 'Success', value: 'success' },
                            { label: 'Failed', value: 'failed' },
                            { label: 'Rolled Back', value: 'rolled_back' },
                            { label: 'Superseded', value: 'superseded' },
                            { label: 'Cancelled', value: 'cancelled' },
                        ]
                    },
                },
                {
                    label: 'Progress',
                    key: 'percent',
                    formatter: (val) => (val === null || val === undefined) ? '' : `${val}%`,
                },
                {
                    label: 'Source',
                    key: 'source',
                    formatter: "default('—')",
                    visibility: 'lg',
                },
                {
                    label: 'Error',
                    key: 'error',
                    // Device-reported OTA error (audit #293 H1) — escape, not raw HTML. Note: the same
                    // device error/stage strings also reach DeviceHistory.metadata server-side; if a
                    // history-metadata surface is ever rendered, escape them there too.
                    formatter: (val) => (val ? dataFormatter.escapeHtml(val) : ''),
                    visibility: 'md',
                },
            ],

            contextMenu: [
                { label: 'Cancel queued update', icon: 'bi bi-x-circle', action: 'cancel-queued' },
            ],

            emptyMessage: 'No OTA activity for this group yet.',

            showAdd: false,
            clickAction: 'none',
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,
            showRefresh: true,

            tableViewOptions: {
                hideActivePillNames: ['device__group'],
            },

            tableOptions: {
                striped: false,
                bordered: false,
                hover: true,
                responsive: true,
            },

            ...options,
        });

        this._onOtaProgress = this._onOtaProgress.bind(this);
        this._otaRefreshTimer = null;
    }

    async onInit() {
        await super.onInit();
        this.getApp().events.on('device:ota_progress', this._onOtaProgress);
    }

    onDestroy() {
        this.getApp().events.off('device:ota_progress', this._onOtaProgress);
        if (this._otaRefreshTimer) clearTimeout(this._otaRefreshTimer);
    }

    // Live refresh: throttle bursts of progress events into one refetch every ~2s,
    // and only while this page is the one on screen.
    _onOtaProgress() {
        if (this._otaRefreshTimer) return;
        this._otaRefreshTimer = setTimeout(() => {
            this._otaRefreshTimer = null;
            const current = this.getApp().getCurrentPage?.();
            if (current && current.pageName !== this.pageName) return;
            if (this.collection?.restEnabled) this.collection.fetch();
        }, 2000);
    }

    // Row context-menu action. The menu item shows on every row (framework menus
    // are static, built once with data-id) — the status guard here plus the
    // server keep a non-queued row safe. group is scope-only (see the trigger
    // endpoint); app.groupId() names the active group.
    async onActionCancelQueued(event, el) {
        const app = this.getApp();
        const id = el.getAttribute('data-id');
        const row = this.collection.get(id);
        if (row?.get('status') !== 'queued') {
            app.toast.info('Only queued updates can be cancelled');
            return;
        }
        const confirmed = await Dialog.confirm({
            title: 'Cancel Queued Update?',
            message: 'Stop the update that is waiting to install when this device reconnects?',
            confirmText: 'Cancel Update',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        const ok = await cancelQueuedUpdate(app, id, app.groupId());
        if (ok) this.collection.fetch();
    }
}
